import Card from "../../../../../components/layouts/card/card";
import PolicyStore from "../../../stores/policy-store";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import { Input } from "../../../../../components/inputs/input/input";
import { observer } from "mobx-react";
import { IOption, SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import { SelectDropdownGrouped } from "../../../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import { NOTE_TYPE, Note } from "../../../../../components/note/note";
import ListLayout from "../../../../../components/layouts/list-layout/list-layout";
import config from "../../../../../config";
import { Toggle } from "../../../../../components/inputs/toggle/toggle";
import AHref from "../../../../../components/ahref/ahref";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import { IValidationGroup } from "../../../../../hooks/validation-group/validation-group-hook";
import { ExpandCallapseStore } from "../../../../../hooks/expand-collapse-hook";
import SelectDropdownMulti from "../../../../../components/inputs/select-dropdown-multi/select-dropdown-multi";
import currentPortalStore from "../../../../../stores/current-portal-store";
import { FEATURES } from "../../../../../models/Plan";
import { AddressEditorTrigger } from "./address-editor-trigger/address-editor-trigger";
import ResolveCapability from "../../../../../components/resolve-capability/resolve-capability";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";

interface IProps {
    policy: PolicyStore;
    validationGroup: IValidationGroup;
    expandCollapseStore: ExpandCallapseStore;
}

const PolicyDetailsCard: React.FC<IProps> = observer(({ policy, validationGroup, expandCollapseStore}) => {
    const returnMethodsOptions = policy.returnMethodsPreset.getOptionsPreset(policy);
    const selectedReturnMethod = returnMethodsOptions.find(rm => rm.value === policy.returnMethodId);

    const usedForOptions = policy.getUsedForOptions();
    const selectedReturnSources = usedForOptions.filter(o => policy.availability?.find(a => a.used_for === o.value));

    const resolutionOptions = policy.resolutionPreset.getOptionsPreset();
    const getSelectedResolution = () => {
        for (let i in resolutionOptions) {
            const resolution = resolutionOptions[i].options.find((resolution: IOption) => resolution.value === policy.resolutionId);
            if (resolution) {
                return resolution;
            }
        }
    }

    const returnMethodsLink = `${config.apiUrl}/dashboard/settings/return-methods`;
    const returnMethodNoteData = policy.returnMethodsPreset.getNoteData(policy);
    const returnMethodNoteTextMap = {
        [NOTE_TYPE.WARNING as NOTE_TYPE]: (
            <>
                The pre-paid shipping label is not properly set!<br />
                Save your changes and go to Settings {'>'} Return Methods {'>'} Ship with a pre-paid
                shipping label {'>'} Shipping label settings or <AHref href={returnMethodsLink} target="_parent">
                    <FontBody2 color={FONT_COLOR.ACCENT1}>go without saving</FontBody2>
                </AHref>
            </>
        ),
        [NOTE_TYPE.INFORMATION as NOTE_TYPE]: (
                <>
                    Shipping label generated by
                    { policy.hasRCRM
                        ? ' Return Guard Shipping'
                        : policy.hasSBR
                            ? ` ${returnMethodNoteData?.aggregator}`
                            : ` ${returnMethodNoteData?.aggregator} ${returnMethodNoteData?.carrier && `-${returnMethodNoteData?.carrier}`} ${returnMethodNoteData?.service && `-${returnMethodNoteData?.service}`}`

                    }
                </>
        )
    };

    /** We will disable coverage toggle only if the selected RM is RCRM and the toggle is on */
    const disableCoverageToggle: boolean = policy.hasRCRM && policy.isCoverage;

    return (
        <Card
            toolbar={{
                title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>Details</FontBody1>
            }}
            isExpanded={expandCollapseStore.getIsExpanded('details-card')}
            expandCallback={(v) => expandCollapseStore.setExpanded('details-card', v)}
        >
            <FormFieldLine
                label='Policy Name'
                note='Define how this policy rule will be displayed to customers in the portal.'
            >
                <Input value={policy.name} onChange={policy.setName} validationGroup={validationGroup} required={true} />
            </FormFieldLine>
            <FormFieldLine
                label='Apply to'
                note='Define where this policy rule can be used.'
            >
                <SelectDropdownMulti
                    options={usedForOptions}
                    value={selectedReturnSources}
                    onChange={v => policy.setAvailability(v as IOption[])}
                    validationGroup={validationGroup}
                    required={true}
                />
            </FormFieldLine>
            <FormFieldLine
                label='Resolution'
                note="Select the resolution associated with this policy rule."
            >
                <SelectDropdownGrouped
                    options={resolutionOptions}
                    value={getSelectedResolution()}
                    onChange={(v) => policy.attemptToChangeResolutionId(Number(v?.value))}
                    validationGroup={validationGroup}
                    required={true}
                />
            </FormFieldLine>
            {policy.showProductExchangeInfo && (
                <Note
                    body={<>
                        Some states have tax exemptions for certain product types and prices. <br/>
                        To get help setting up your return policy for such situations, contact support.
                    </>}
                />
            )}
            <FormFieldLine
                label='Return Method'
                note={<>
                    Select the return method associated with this policy rule.<br/>
                    Keep in mind that some return methods require manual approval.
                </>}>
                <ListLayout>
                    <SelectDropdown
                        options={returnMethodsOptions}
                        value={selectedReturnMethod}
                        onChange={(v) => policy.attemptToChangeReturnMethodId(Number(v?.value))}
                        validationGroup={validationGroup}
                        required={true}
                    />
                    {!!returnMethodNoteData && <Note
                        type={returnMethodNoteData.type}
                        body={returnMethodNoteTextMap[returnMethodNoteData.type]}/>}
                </ListLayout>
            </FormFieldLine>

            {!!currentPortalStore.isFeatureAllowed(FEATURES.COVERAGE) &&
                <FormFieldLine
                    label='Display for Returns Covered by Return Guard'
                    note={<>Enable to offer this policy rule to customers who have purchased Return Guard coverage<br/> and are returning a covered order.</>}
                >
                    <Toggle
                        checked={policy.isCoverage}
                        label=""
                        onChange={v => policy.setIsCoverage(v)}
                        disabled={disableCoverageToggle}
                    />
                </FormFieldLine>

            }

            {!!currentPortalStore.isFeatureAllowed(FEATURES.BETA_EU_RIGHT_OF_WITHDRAWAL) &&
                <FormFieldLine
                    label='Include withdrawal acknowledgement in Request approved email'
                    note={<>When enabled, shoppers who complete a return under this policy receive withdrawal acknowledgement text in the Request approved email (in addition to the standard approval message). Use only for policy rules that handle EU right-of-withdrawal returns.</>}
                >
                    <Toggle
                        checked={policy.includeEuRightOfWithdrawalEmail}
                        label=""
                        onChange={v => policy.setIncludeEuRightOfWithdrawalEmail(v)}
                    />
                </FormFieldLine>
            }

            {policy.isMultipleShipmentsSupported &&
                <FormFieldLine label='Ask the customer how many packages they need to ship'>
                    <Toggle
                        checked={policy.requestMultipleShipments}
                        label={""}
                        onChange={v => policy.setRequestMultipleShipments(v)}
                    />
                </FormFieldLine>
            }

            {policy.isDisplayAsDisableSupported && <FormFieldLine
                label='Display as Disabled'
                note='When enabled, this policy rule will be displayed as disabled in the portal when no Refund to Payment Method policy rules are eligible.'
            >
                <Toggle
                    checked={policy.displayAsDisabled}
                    label={""}
                    onChange={v => policy.setDisplayAsDisabled(v)}
                />
            </FormFieldLine>}

            {!!policy.showRequireAddress && <>
                <ResolveCapability capability={PROFILE_CAPABILITIES.RETURN_POLICY_RULE}>
                    {({ isCustomerAddressValidationRequirementSupported, isCustomerAddressPrefillSupported, isCustomerAddressFieldsCustomizationSupported }) => (
                        <>
                            {isCustomerAddressValidationRequirementSupported && <FormFieldLine
                                label='Require customers to validate address'
                                note='When set to on, the customer must fill in their address before the RMA is created.'
                            >
                                <Toggle
                                    checked={policy.requireAddress}
                                    label={""}
                                    onChange={v => policy.setRequireAddress(v)}
                                />
                            </FormFieldLine>}
                            {isCustomerAddressPrefillSupported && <FormFieldLine
                                label='Prefill Customer Address'
                                note='When enabled, the address validation fields will be automatically prefilled with the shipping address of the returned order.'
                                disabled={!policy.requireAddress}
                            >
                                <Toggle
                                    checked={policy.prefillAddress}
                                    label={""}
                                    onChange={v => policy.setPrefillAddress(v)}
                                    disabled={!policy.requireAddress}
                                />
                            </FormFieldLine>}

                            {isCustomerAddressValidationRequirementSupported && isCustomerAddressFieldsCustomizationSupported && (
                                <FormFieldLine
                                    label="Address Fields Display & Validation"
                                    note="Configure which customer information fields are shown on the portal and set their validation requirements. You can display or hide each field and make displayed fields either mandatory or optional."
                                    disabled={!policy.requireAddress}
                                >
                                    <AddressEditorTrigger
                                        addressRequirements={
                                            policy.addressRequirementsStore
                                        }
                                    />
                                </FormFieldLine>
                            )}

                            {isCustomerAddressValidationRequirementSupported && !isCustomerAddressFieldsCustomizationSupported && (
                                <FormFieldLine
                                    label="Zip Code field is mandatory"
                                    note="When set to on, the customer must fill in their address before the RMA is created."
                                    disabled={!policy.requireAddress}
                                >
                                    <Toggle
                                        checked={
                                            policy.addressRequirementsStore.zip.required
                                        }
                                        label={""}
                                        onChange={(v) => policy.addressRequirementsStore.setZipRequired(v)}
                                        disabled={!policy.requireAddress}
                                    />
                                </FormFieldLine>
                            )}
                        </>
                    )}
                </ResolveCapability>

            </>}

        </Card>
    )
});

export default PolicyDetailsCard;
